import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { createUserAPI, updateUserAPI, getAllUsersAPI, sendPasswordResetAPI, addUserToServerAPI } from '../services/serverService';

const Spinner = () => <div className="spinner"></div>;

const AddServiceUserModal = ({ serverId, closeModal, refreshList, userToEdit, existingUsers = [] }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(userToEdit);

    const [view, setView] = useState(isEditMode ? 'create' : 'search');
    const [searchTerm, setSearchTerm] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const [formData, setFormData] = useState({
        username: '',
        fullName: '',
        email: '',
        password: '',
        phoneCode: '+44',
        phoneNumber: '',
        dob: '',
        addressStreet: '',
        addressLine2: '',
        addressTown: '',
        addressCounty: '',
        addressPostcode: '',
        addressCountry: '',
    });

    const [isSaving, setIsSaving] = useState(false);
    const [apiError, setApiError] = useState('');

    const [resetEmailSent, setResetEmailSent] = useState(false);

    const handleSendResetEmail = async () => {
        const confirmAlert = window.confirm(`Send a password reset email to ${formData.email}?`);
        if (!confirmAlert) return;

        try {
            const targetUser = userToEdit;
            const targetId = targetUser.username || targetUser.id || targetUser.uid || targetUser.email;

            await sendPasswordResetAPI({
                userId: targetId,
                email: formData.email,
                username: formData.username,
                fullName: formData.fullName
            });
            
            setResetEmailSent(true);
            alert(`Password reset email sent safely to ${formData.email}!`);
        } catch (error) {
            console.error("Reset Password Error:", error);
            alert("Failed to send reset email. Please check the logs.");
        }
    };

    useEffect(() => {
        if (isEditMode && userToEdit) {
            setView('create');
            const addr = userToEdit.address || {};
            setFormData({
                username: userToEdit.username || '',
                fullName: userToEdit.fullName || '',
                email: userToEdit.email || '',
                password: '', 
                phoneCode: userToEdit.phoneCode || '+44',
                phoneNumber: userToEdit.phoneNumber || '',
                dob: userToEdit.dob || '',
                addressStreet: addr.firstLineOfAddress || '',
                addressLine2: addr.secondLineOfAddress || '',
                addressTown: addr.town || '',
                addressCounty: addr.county || '',
                addressPostcode: addr.postalCode || '',
                addressCountry: addr.country || '',
            });
        }
    }, [isEditMode, userToEdit]);

    const { fullName } = formData;

    useEffect(() => {
        if (!isEditMode && view === 'create') {
            if (fullName.trim() === '') {
                setFormData(prev => ({ ...prev, username: '' }));
                return;
            }
            const nameParts = fullName.trim().split(/\s+/);
            const suggestion = nameParts.map((part, index) => {
                if (index === 0) return part.toLowerCase();
                return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            }).join('');
            setFormData(prev => ({ ...prev, username: suggestion }));
        }
    }, [fullName, isEditMode, view]);

    useEffect(() => {
        if (!isEditMode && view === 'search') {
            const fetchUsers = async () => {
                setIsSearching(true);
                try {
                    const response = await getAllUsersAPI();
                    setAllUsers(response.data.data);
                } catch (err) {
                    setApiError("Could not load user list.");
                } finally {
                    setIsSearching(false);
                }
            };
            fetchUsers();
        }
    }, [isEditMode, view]);

    useEffect(() => {
        if (searchTerm === '') {
            setSearchResults([]);
            return;
        }
        const lowerTerm = searchTerm.toLowerCase();
        const results = allUsers.filter(user => {
            const uName = user?.username?.toLowerCase() || "";
            const fName = user?.fullName?.toLowerCase() || "";
            const email = user?.email?.toLowerCase() || "";
            
            return uName.includes(lowerTerm) || fName.includes(lowerTerm) || email.includes(lowerTerm);
        });
        setSearchResults(results);
    }, [searchTerm, allUsers]);

    const handleAddExistingUser = async (user) => {
        setIsSaving(true);
        setApiError('');
        try {
            if ((user.role || 'unknown') !== 'serviceUser') {
                setApiError(`This user is a(n) ${user.role || 'unknown'} and cannot be added as a Service User.`);
                setIsSaving(false);
                return;
            }
            await addUserToServerAPI(serverId, user.username || user.id);
            alert(`Existing service user '${user.fullName}' successfully added.`);
            refreshList();
            closeModal();
        } catch (err) {
            setApiError("Failed to add existing user.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'username') value = value.replace(/\s+/g, '');
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    // SURGICAL FIX: Added bypassLinkUid parameter to handle recursive linking
    const handleSubmit = async (e, shouldRedirect = false, bypassLinkUid = null) => {
        if (e) e.preventDefault();
        setIsSaving(true);
        setApiError('');

        const addressObject = {
            firstLineOfAddress: formData.addressStreet,
            secondLineOfAddress: formData.addressLine2,
            town: formData.addressTown,
            county: formData.addressCounty,
            postalCode: formData.addressPostcode,
            country: formData.addressCountry,
            fullAddress: `${formData.addressStreet}, ${formData.addressTown}, ${formData.addressPostcode}, ${formData.addressCountry}`
        };

        const { addressStreet, addressLine2, addressTown, addressCounty, addressPostcode, addressCountry, ...coreData } = formData;
        const payload = { ...coreData, address: addressObject };

        try {
            let newUserId = null;
            if (isEditMode) {
                const targetId = userToEdit.username || userToEdit.id || userToEdit.uid || userToEdit.email;
                await updateUserAPI(targetId, payload);
                alert('Service User updated successfully!');
            } else {
                const createPayload = { ...payload, role: 'serviceUser', serverIds: [serverId] };
                
                if (bypassLinkUid) createPayload.forceLinkUid = bypassLinkUid;

                const response = await createUserAPI(createPayload);

                if (response.data && response.data.status === 'requires_linking') {
                    setIsSaving(false);
                    const proceed = window.confirm(response.data.message);
                    if (proceed) {
                        return handleSubmit(null, shouldRedirect, response.data.uid);
                    } else {
                        return; // User cancelled
                    }
                }

                newUserId = formData.username;
                alert('New service user created successfully!');
            }
            refreshList();

            if (shouldRedirect && newUserId) {
                navigate(`/servers/${serverId}/service-users/${newUserId}`, { state: { openEditModal: true } });
            } else {
                closeModal();
            }
        } catch (err) {
            setApiError(err.response?.data?.message || 'Operation failed.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderSearchView = () => (
        <>
            <h2>Add Service User to Server</h2>
            <p className="modal-subtitle">Search for an existing global service user or create a new one.</p>
            <div className="form-group full-width">
                <label>Search by Name, Email, or Username</label>
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="e.g., Anne Jones" />
            </div>
            {isSearching && <p>Loading users...</p>}
            {apiError && <p className="error-message">{apiError}</p>}
            <div className="search-results-container">
                {searchResults.length > 0 ? (
                    searchResults.map(user => (
                        <div className="search-result-item" key={user.id}>
                            <div>
                                <strong className="search-result-name">{user.fullName}</strong>
                                <span className={`search-result-role ${(user.role || 'unknown').toLowerCase()}`}>{(user.role || 'unknown').charAt(0).toUpperCase() + (user.role || 'unknown').slice(1)}</span>
                                <div className="search-result-details">{user.email} | {user.username}</div>
                            </div>
                            <button 
                                className="button-primary add-existing-button" 
                                onClick={() => handleAddExistingUser(user)} 
                                disabled={isSaving || existingUsers.some(su => su.uid === user.uid || su.id === user.uid || su.username === user.username)}
                            >
                                {existingUsers.some(su => su.uid === user.uid || su.id === user.uid || su.username === user.username) ? 'Already Added' : 'Add'}
                            </button>
                        </div>
                    ))
                ) : (!isSearching && searchTerm.length > 0 && <p className="no-data-cell">No users found.</p>)}
            </div>
            <div className="modal-actions" style={{ justifyContent: 'space-between', marginTop: '20px' }}>
                <button type="button" className="button-secondary" onClick={closeModal} disabled={isSaving}>Cancel</button>
                <button type="button" className="button-primary" onClick={() => setView('create')}>+ Create New Service User</button>
            </div>
        </>
    );

    const renderCreateEditView = () => (
        <>
            <h2>{isEditMode ? 'Edit Service User' : 'Create New Service User'}</h2>
            <form>
                <div className="form-grid">
                    <div className="form-group"><label>Full Name</label><input name="fullName" value={formData.fullName} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Username</label><input name="username" value={formData.username} onChange={handleChange} required disabled={isEditMode} /></div>
                    <div className="form-group"><label>Email Address</label><input type="email" name="email" value={formData.email} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Password</label><input type="password" name="password" placeholder={isEditMode ? "Leave blank to keep unchanged" : ""} onChange={handleChange} required={!isEditMode} /></div>
                    
                    <div className="form-group">
                        <label>Phone Number</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input 
                                type="text" 
                                name="phoneCode" 
                                value={formData.phoneCode} 
                                onChange={handleChange} 
                                placeholder="+44" 
                                style={{ width: '60px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                            />
                            <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="phoneNumber" style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                        </div>
                    </div>

                    <div className="form-group"><label>Date of Birth</label><input type="date" name="dob" value={formData.dob} onChange={handleChange} required /></div>
                    
                    <div className="form-group"><label>Street Address</label><input name="addressStreet" value={formData.addressStreet} onChange={handleChange} required placeholder="e.g. 123 Main St" /></div>
                    <div className="form-group"><label>Address Line 2 (Optional)</label><input name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder="e.g. Apt 4B" /></div>
                    <div className="form-group"><label>Town / City</label><input name="addressTown" value={formData.addressTown} onChange={handleChange} required /></div>
                    <div className="form-group"><label>County</label><input name="addressCounty" value={formData.addressCounty} onChange={handleChange} /></div>
                    <div className="form-group"><label>Postcode</label><input name="addressPostcode" value={formData.addressPostcode} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Country</label><input name="addressCountry" value={formData.addressCountry} onChange={handleChange} /></div>
                </div>

                {isEditMode && (
                    <div style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '6px' }}>
                        <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}><strong>Security Options:</strong></p>
                        <button 
                            type="button" 
                            className="button-secondary"
                            onClick={handleSendResetEmail}
                            disabled={resetEmailSent}
                        >
                            {resetEmailSent ? "Reset Email Sent ✓" : "Send Password Reset Email"}
                        </button>
                    </div>
                )}

                {apiError && <p className="error-message">{apiError}</p>}

                <div className="modal-actions" style={{ gap: '10px' }}>
                    <button type="button" className="button-secondary" onClick={isEditMode ? closeModal : () => setView('search')}>{isEditMode ? 'Cancel' : 'Back'}</button>
                    <button type="button" className={isEditMode ? "button-primary" : "button-primary-outline"} disabled={isSaving} onClick={(e) => handleSubmit(e, false)}>
                        {isSaving ? <Spinner /> : (isEditMode ? 'Save Changes' : 'Quick Create')}
                    </button>
                    {!isEditMode && (
                        <button type="button" className="button-primary" disabled={isSaving} style={{ display: 'flex', alignItems: 'center', gap: '5px' }} onClick={(e) => handleSubmit(e, true)}>
                            {isSaving ? <Spinner /> : <>Create & Edit Profile <FaArrowRight /></>}
                        </button>
                    )}
                </div>
            </form>
        </>
    );

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <button className="modal-close-button" onClick={closeModal}>&times;</button>
                {view === 'search' ? renderSearchView() : renderCreateEditView()}
            </div>
        </div>
    );
};

export default AddServiceUserModal;